import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { hasAnyExistingMetrics } from '../lib/metricsExistence'
import { 
  calculateAllMetrics, 
  validateMetrics
} from '../utils/financialCalculations'
import CalculatedMetricsPreview from './CalculatedMetricsPreview'

const MonthlyMetricsForm = ({ selectedDate, onSave, existingData, onDataChange, onFirstMetricSaved }) => {
  const { user } = useAuth()
  const { t, i18n } = useTranslation(['dashboard', 'common'])
  const [formData, setFormData] = useState({
    revenue: '',
    expenses: '',
    leads: '',
    customers: '',
    // social_following: ''
  })
  const [calculatedMetrics, setCalculatedMetrics] = useState({})
  const [previousMetrics, setPreviousMetrics] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const customersExceedLeads = formData.customers !== '' && Number(formData.customers) > (Number(formData.leads) || 0)
  const customerError = customersExceedLeads
    ? t('dataEntry.validation.customersExceedLeads')
    : errors.customers === 'Customers cannot exceed leads' ? '' : errors.customers

  // Load existing data when selectedDate or existingData changes
  useEffect(() => {
    if (existingData) {
      setFormData({
        revenue: existingData.revenue?.toString() || '',
        expenses: existingData.expenses?.toString() || '',
        leads: existingData.leads?.toString() || '',
        customers: existingData.customers?.toString() || '',
        // social_following: existingData.social_following?.toString() || ''
      })
    } else {
      setFormData({
        revenue: '',
        expenses: '',
        leads: '',
        customers: '',
        // social_following: ''
      })
    }
  }, [existingData, selectedDate])

  // Fetch previous month's data for growth calculations
  useEffect(() => {
    fetchPreviousMonthData()
  }, [selectedDate])

  // Calculate metrics whenever form data changes
  useEffect(() => {
    const numericData = {
      revenue: parseFloat(formData.revenue) || 0,
      expenses: parseFloat(formData.expenses) || 0,
      leads: parseInt(formData.leads) || 0,
      customers: parseInt(formData.customers) || 0
    }
    
    const metrics = calculateAllMetrics(numericData, previousMetrics)
    setCalculatedMetrics(metrics)
  }, [formData, previousMetrics])


  const fetchPreviousMonthData = async () => {
    if (!user || !selectedDate) return

    try {
      // Calculate previous month
      const prevDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1)
      
      // Get user's business
      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (!business) return

      // Fetch previous month's metrics
      const { data } = await supabase
        .from('monthly_metrics')
        .select('*')
        .eq('business_id', business.id)
        .eq('month', prevDate.getMonth() + 1)
        .eq('year', prevDate.getFullYear())
        .single()

      setPreviousMetrics(data)
    } catch (error) {
      console.log('No previous month data found:', error)
      setPreviousMetrics(null)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Convert to numbers
    const numericData = {
      revenue: parseFloat(formData.revenue) || 0,
      expenses: parseFloat(formData.expenses) || 0,
      leads: parseInt(formData.leads) || 0,
      customers: parseInt(formData.customers) || 0,
      // social_following: parseInt(formData.social_following) || 0
    }

    // Validate data
    const validation = validateMetrics(numericData)
    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    setSaving(true)
    setErrors({})

    try {
      // Get user's business
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (businessError) throw businessError
      const hadAnyMetricsBeforeSave = await hasAnyExistingMetrics(business.id)

      // Prepare data for database
      const metricsData = {
        business_id: business.id,
        month: selectedDate.getMonth() + 1,
        year: selectedDate.getFullYear(),
        ...numericData
      }

      // Check if record exists (for update vs insert)
      const { data: existingRecord } = await supabase
        .from('monthly_metrics')
        .select('id')
        .eq('business_id', business.id)
        .eq('month', selectedDate.getMonth() + 1)
        .eq('year', selectedDate.getFullYear())
        .single()

      let result
      if (existingRecord) {
        // Update existing record
        result = await supabase
          .from('monthly_metrics')
          .update(metricsData)
          .eq('id', existingRecord.id)
      } else {
        // Insert new record
        result = await supabase
          .from('monthly_metrics')
          .insert(metricsData)
      }

      if (result.error) throw result.error

      if (onSave) onSave(metricsData)
      if (onDataChange) await onDataChange()

      if (!hadAnyMetricsBeforeSave && onFirstMetricSaved) {
        onFirstMetricSaved()
        return
      }

      setSuccessMessage(t('dataEntry.saveSuccess'))
      setTimeout(() => setSuccessMessage(''), 3000)

    } catch (error) {
      console.error('Error saving metrics:', error)
      setErrors({ general: t('dataEntry.saveError') })
    } finally {
      setSaving(false)
    }
  }

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      month: 'long', 
      year: 'numeric' 
    })
  }

  return (
    <div className="min-w-0 bg-white border border-gray-200 border-t-[3px] border-t-[#FF1B54] shadow-sm rounded-2xl p-6 sm:p-8">
      <div className="mb-7">
        <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 mb-2">
          {t('dataEntry.title')}
        </h3>
        <p className="text-base leading-6 text-gray-600">
          {t('dataEntry.subtitle', { month: formatDateDisplay(selectedDate), year: '' }).replace(/\s+$/, '')}
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          {/* Revenue */}
          <div>
            <label htmlFor="revenue" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.revenue')} ($)
            </label>
            <input
              type="number"
              id="revenue"
              name="revenue"
              value={formData.revenue}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.revenue ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="10000.00"
            />
            {errors.revenue && (
              <p className="mt-1 text-sm text-red-600">{errors.revenue}</p>
            )}
          </div>

          {/* Expenses */}
          <div>
            <label htmlFor="expenses" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.expenses')} ($)
            </label>
            <input
              type="number"
              id="expenses"
              name="expenses"
              value={formData.expenses}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.expenses ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="7500.00"
            />
            {errors.expenses && (
              <p className="mt-1 text-sm text-red-600">{errors.expenses}</p>
            )}
          </div>

          {/* Leads */}
          <div>
            <label htmlFor="leads" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.leads')}
            </label>
            <input
              type="number"
              id="leads"
              name="leads"
              value={formData.leads}
              onChange={handleInputChange}
              min="0"
              step="1"
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                errors.leads ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="150"
            />
            {errors.leads && (
              <p className="mt-1 text-sm text-red-600">{errors.leads}</p>
            )}
          </div>

          {/* Customers */}
          <div>
            <label htmlFor="customers" className="block text-sm font-medium text-gray-700">
              {t('dataEntry.fields.customersAcquired')}
            </label>
            <input
              type="number"
              id="customers"
              name="customers"
              value={formData.customers}
              onChange={handleInputChange}
              min="0"
              step="1"
              max={formData.leads === '' ? 0 : Number(formData.leads)}
              aria-invalid={Boolean(customerError)}
              className={`mt-2 block w-full h-12 border rounded-lg px-3.5 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54] transition-colors duration-150 ${
                customerError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="25"
            />
            {customerError && (
              <p className="mt-1 text-sm text-red-600">{customerError}</p>
            )}
          </div>

          {/* Social Following (temporarily removed) */}
          {/**
           * TODO: Re-enable social_following when we decide how/where it should be used.
           * Keeping the DB column is fine, but we are intentionally not collecting it in the UI for now.
           */}
          {/*
          <div className="md:col-span-2">
            <label htmlFor="social_following" className="block text-sm font-medium text-gray-700">
              Social Media Following
            </label>
            <input
              type="number"
              id="social_following"
              name="social_following"
              value={formData.social_following}
              onChange={handleInputChange}
              min="0"
              step="1"
              className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                errors.social_following ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="1250"
            />
            {errors.social_following && (
              <p className="mt-1 text-sm text-red-600">{errors.social_following}</p>
            )}
          </div>
          */}
        </div>

        {/* Real-time Calculations Display */}
        {(formData.revenue || formData.expenses) && (
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">{t('dataEntry.calculatedPreview')}</h4>
            <CalculatedMetricsPreview metrics={calculatedMetrics} previousMetrics={previousMetrics} />
            <p className="text-sm text-gray-600 text-center mt-8">
              {t('dataEntry.viewDashboardHint')}
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || customersExceedLeads}
            className="h-12 bg-[#FF1B54] hover:bg-[#E1174C] text-white px-7 rounded-lg font-semibold shadow-sm hover:shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {saving ? t('dataEntry.saving') : t('dataEntry.saveMetrics')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default MonthlyMetricsForm
